import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  forwardRef
} from 'react';
import { hex_md5 } from 'utils/md5';
import request from 'utils/request';
import config from 'config/Config';
import cacheManager from 'utils/cache/cacheManager';
import { loadLanguagePackage } from './initLanguage';

// 保存原始的 createElement 方法
const originalCreateElement = React.createElement;
const originChildren = React.Children;
const pendingTranslationMap = new Map();
const queuedTranslationMap = new Map();
const TRANSLATION_BATCH_SIZE = 6;
let translationFlushScheduled = false;

const LanguageContext = createContext();
const useLanguage = () => useContext(LanguageContext);
// 国际化配置Provider
const LanguageProvider = (props) => {
  const { children, ...rest } = props;

  return <LanguageContext.Provider {...rest}>{children}</LanguageContext.Provider>;
};

// 装饰器函数
const withTranslation = (Dom) => {
  return class WithTranslation extends React.Component {
    static contextType = LanguageContext;

    render() {
      return <Dom {...this.props} useContext={this.context} />;
    }
  };
};

const t = (text) => {
  return text;
};

const getCurrentLng = () => window.currentLng || localStorage.getItem('lng') || 'cn';

const isNeedTranslation = (text) => {
  return !!(
    text &&
    getCurrentLng() != 'cn' &&
    typeof text == 'string' &&
    /[\u4e00-\u9fa5]/.test(text)
  );
};

const getTranslationCache = () => {
  return cacheManager.get(`translation_${getCurrentLng()}`) || {};
};

const getCachedTranslation = (text) => {
  const translationJson = getTranslationCache();
  return translationJson[text];
};

const createDeferred = () => {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};

// 翻译api函数
const translation = (text, save = true) => {
  const lng = getCurrentLng();
  const translationJson = getTranslationCache();

  if (lng == 'cn') {
    return new Promise((resolve) => resolve(text));
  }

  // 先尝试命中缓存；缓存缺失时优先补拉对应语言 JSON，再兜底翻译接口
  return new Promise((resolve) => {
    const resolveByApiFallback = () => {
      const latestTranslationJson = cacheManager.get(`translation_${lng}`) || {};
      let params = {
        sign: hex_md5(text + 'yofoto333'),
        text,
        isSave: save ? 1 : 0
      };

      request({
        url: 'translate/record/translate',
        host: config.HOST_PROMOTE,
        method: 'post_json',
        data: params
      }).then((res) => {
        const { data, resultCode } = res;
        try {
          if (resultCode == 0) {
            latestTranslationJson[text] = data.translateText;
            cacheManager.set(`translation_${lng}`, latestTranslationJson);
            resolve(data.translateText);
          } else {
            latestTranslationJson[text] = data.translateText;
            cacheManager.set(`translation_${lng}`, latestTranslationJson);
            resolve(text);
          }
        } catch (e) {
          resolve(text);
        }
      });
    };

    const translationJson = cacheManager.get(`translation_${lng}`) || {};
    if (translationJson[text]) {
      resolve(translationJson[text]);
      return;
    }

    loadLanguagePackage(lng)
      .then((packageJson) => {
        if (packageJson && packageJson[text]) {
          resolve(packageJson[text]);
          return;
        }

        const latestTranslationJson = cacheManager.get(`translation_${lng}`) || {};
        if (latestTranslationJson[text]) {
          resolve(latestTranslationJson[text]);
          return;
        }

        resolveByApiFallback();
      })
      .catch(() => {
        resolveByApiFallback();
      });
  });
};

const runTranslationQueue = async (queueItems) => {
  for (let i = 0; i < queueItems.length; i += TRANSLATION_BATCH_SIZE) {
    const chunk = queueItems.slice(i, i + TRANSLATION_BATCH_SIZE);
    await Promise.all(
      chunk.map((item) => {
        return translation(item.text, item.save)
          .then((content) => {
            item.resolve(content);
          })
          .catch((error) => {
            item.reject(error);
          })
          .finally(() => {
            pendingTranslationMap.delete(item.taskKey);
          });
      })
    );
  }
};

const scheduleTranslationFlush = () => {
  if (translationFlushScheduled) {
    return;
  }

  translationFlushScheduled = true;
  Promise.resolve().then(() => {
    translationFlushScheduled = false;
    const queueItems = Array.from(queuedTranslationMap.values());
    queuedTranslationMap.clear();
    if (!queueItems.length) {
      return;
    }
    runTranslationQueue(queueItems);
  });
};

const prefetchTranslation = (text, save = true) => {
  if (!isNeedTranslation(text)) {
    return Promise.resolve(text);
  }

  const cachedText = getCachedTranslation(text);
  if (cachedText) {
    return Promise.resolve(cachedText);
  }

  const taskKey = `${getCurrentLng()}__${text}`;
  if (pendingTranslationMap.has(taskKey)) {
    return pendingTranslationMap.get(taskKey);
  }

  const deferred = createDeferred();
  pendingTranslationMap.set(taskKey, deferred.promise);
  queuedTranslationMap.set(taskKey, {
    taskKey,
    text,
    save,
    resolve: deferred.resolve,
    reject: deferred.reject
  });
  scheduleTranslationFlush();
  return deferred.promise;
};

const getInitialTranslationText = (text, save = true) => {
  if (!isNeedTranslation(text)) {
    return text;
  }

  const cachedText = getCachedTranslation(text);
  if (cachedText) {
    return cachedText;
  }

  prefetchTranslation(text, save);
  return '';
};

// 翻译组件核心core
const transCore = (props) => {
  const { text, save, callback, otherParams = {} } = props;
  const translationJson = getTranslationCache();
  // 比对缓存中的json表 如果当前语言非中文 则请求接口翻译
  const langText = translationJson[text];
  if (langText && langText != text) {
    callback(langText, ...otherParams);
  } else if (isNeedTranslation(text)) {
    prefetchTranslation(text, save).then((content) => {
      callback(content, ...otherParams);
    });
  } else {
    callback(text, ...otherParams);
  }
};

// 无法被拦截器处理到的string|reactNode处理
const translationByCallBack = (text, callback) => {
  if (getCurrentLng() == 'cn') {
    callback(text);
  } else {
    if (typeof text == 'string') {
      if (/[\u4e00-\u9fa5]/.test(text)) {
        transCore({
          text: text,
          save: true,
          callback: (res) => callback(res)
        });
      } else {
        callback(text);
      }
    } else if (typeof text == 'object') {
      const children = text.props.children;
      if (Array.isArray(children)) {
        text.props.children = originChildren.map(children, (child) => {
          if (typeof child === 'string' && /[\u4e00-\u9fa5]/.test(child)) {
            return originalCreateElement(I18nText, {
              text: child,
              save: !text.props['data-unsave']
            });
          }
          return child;
        });
      }
      callback(text);
    }
  }
};

const useTranslationText = (text, save = true) => {
  const [translationText, setTranslationText] = useState(() =>
    getInitialTranslationText(text, save)
  );

  useLayoutEffect(() => {
    let active = true;
    if (!isNeedTranslation(text)) {
      setTranslationText(text);
      return () => {
        active = false;
      };
    }

    const cachedText = getCachedTranslation(text);
    if (cachedText) {
      setTranslationText(cachedText);
      return () => {
        active = false;
      };
    }

    prefetchTranslation(text, save).then((content) => {
      if (active) {
        setTranslationText(content);
      }
    });

    return () => {
      active = false;
    };
  }, [text, save]);

  return translationText;
};

// 国际化语言-text
const I18nText = ({ text, save = true }) => {
  const translationText = useTranslationText(text, save);
  return translationText;
};

// 国际化语言-Input
const I18nInput = forwardRef(({ placeholder, ref, ...props }, refs) => {
  const translationText = useTranslationText(placeholder);

  return originalCreateElement('input', { ...props, ref: refs, placeholder: translationText });
});

// 国际化语言-TextArea
const I18nTextarea = forwardRef(({ placeholder, ref, ...props }, refs) => {
  const translationText = useTranslationText(placeholder);

  return originalCreateElement('textarea', {
    ...props,
    ref: refs,
    placeholder: translationText
  });
});

// 国际化语言-Toast提示
const ToastMessage = (toast) => {
  return (text, ...arg) => {
    transCore({ text, callback: toast, otherParams: arg });
  };
};
// 国际化语言目录
const Language_MAP = window.Language_MAP || {};

// 语言列表
const Language_List = window.Language_List || [];

export {
  LanguageProvider, // Provider组件
  LanguageContext, // context实例
  withTranslation, // 装饰器函数
  useLanguage, // hooks获取context实例
  originalCreateElement, // 保存原始的 createElement 方法
  originChildren,
  translation, // 翻译api函数
  prefetchTranslation,
  translationByCallBack, // 无法被拦截器处理到的string|reactNode处理
  t, // mobx使用i18n实例
  transCore, // 翻译核心函数
  I18nText, // 翻译组件
  I18nInput, // 翻译组件
  I18nTextarea, // 翻译组件
  ToastMessage,
  Language_MAP, // 语言目录
  Language_List // 语言列表
};



// WEBPACK FOOTER //
// ./src/config/language/context.js